import React from 'react';

import { getSidebarNavColorConfig, getSidebarNavIndicatorClass } from './sidebarNavStyles';

export default function SidebarNavItem({
  icon,
  label,
  isActive = false,
  onClick,
  showLabels = false,
  activeColor = 'blue',
  title,
  badge = null,
  hasChanges = false,
  variant = 'default',
  tourTarget,
  note = null,
  trailing = null,
  ariaHasPopup,
  ariaExpanded,
}) {
  const colorConfig = getSidebarNavColorConfig(activeColor);
  const indicatorClass = getSidebarNavIndicatorClass(activeColor);
  const badgeLabel = typeof badge === 'number' && badge > 0 ? (badge > 99 ? '99+' : String(badge)) : null;
  const isDanger = variant === 'danger';
  const isMuted = variant === 'muted';

  // The full name is the tooltip; a label too long for the open sidebar ends in an ellipsis.
  const tooltip = title ?? `${badgeLabel ? `${label} (${badgeLabel} unread)` : label}${note ? ` · ${note}` : ''}${hasChanges ? ' · Changed in this proposal' : ''}`;

  return (
    <button
      type="button"
      data-workspace-tour={tourTarget}
      onClick={onClick}
      title={tooltip}
      aria-label={tooltip}
      aria-current={isActive && !ariaHasPopup ? 'page' : undefined}
      aria-haspopup={ariaHasPopup}
      aria-expanded={ariaExpanded}
      className={`group/item relative flex w-full min-w-0 items-center rounded-lg text-left transition-[background-color,color,padding] duration-200 ease-out ${
        showLabels ? 'justify-start px-2.5 py-2' : 'justify-start px-2.5 py-2.5'
      } ${
        isActive
          ? `bg-slate-900/[0.06] ${colorConfig.active}`
          : isDanger
            ? 'text-rose-600 hover:bg-rose-50/80'
            : 'text-slate-600 hover:bg-slate-900/[0.04]'
      }`}
    >
      {isActive ? (
        <span
          className={`absolute left-0 top-1/2 h-4 w-[3px] -translate-y-1/2 rounded-r-full ${indicatorClass} ${
            showLabels ? 'opacity-100' : 'opacity-0 transition-opacity duration-200 group-hover/sidebar:opacity-100 group-data-[open=true]/sidebar:opacity-100'
          }`}
          aria-hidden
        />
      ) : null}
      <span className="relative flex shrink-0 items-center justify-center [&_svg]:h-[18px] [&_svg]:w-[18px]">
        {icon}
        {hasChanges ? (
          <span aria-hidden="true" data-testid="branch-change-mark" className="absolute -right-1 -top-1 h-2 w-2 rounded-full bg-indigo-500 ring-2 ring-slate-50" />
        ) : null}
        {badgeLabel && !showLabels ? (
          <span className="absolute -right-1 -top-1 inline-flex h-3.5 min-w-[0.875rem] items-center justify-center rounded-full bg-violet-600 px-0.5 text-[9px] font-semibold leading-none text-white">
            {badgeLabel}
          </span>
        ) : null}
      </span>
      <span
        className={`ml-0 min-w-0 overflow-hidden text-[13px] font-medium tracking-[-0.01em] transition-[max-width,opacity,margin] duration-200 ease-out ${
          showLabels
            ? 'ml-2.5 flex-1 opacity-100'
            : 'max-w-0 opacity-0 group-hover/sidebar:ml-2.5 group-hover/sidebar:max-w-[11rem] group-hover/sidebar:opacity-100 group-data-[open=true]/sidebar:ml-2.5 group-data-[open=true]/sidebar:max-w-[11rem] group-data-[open=true]/sidebar:opacity-100'
        } ${isActive ? 'text-slate-900' : isDanger ? 'text-rose-600' : isMuted ? 'text-slate-400' : 'text-slate-600'}`}
      >
        <span className="block truncate">{label}</span>
      </span>
      {trailing && !badgeLabel ? (
        <span className={`ml-auto shrink-0 pl-1 ${showLabels ? 'flex' : 'hidden group-hover/sidebar:flex group-data-[open=true]/sidebar:flex'}`}>{trailing}</span>
      ) : null}
      {badgeLabel && showLabels ? (
        <span className="ml-auto shrink-0 inline-flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-violet-600 px-1 text-[10px] font-semibold text-white">
          {badgeLabel}
        </span>
      ) : null}
    </button>
  );
}
